// Modular LoDash imports
import _cloneDeep from 'lodash/cloneDeep';
import _has from 'lodash/has';

// Class module imports
import EventTracking from '@classes/EventTracking';

// Import utlitiy functions
import userAgent from '@utilities/userAgent';

const Events = new EventTracking;

/**
 * The core error handling class.
 */
class ErrorHandler {
    /**
     * Error handler constructor.
     *
     * @constructor
     */
    listener () {
        // If error logging has been turned off or the UA is a bot then exit the constructor
        // Most bots can't won't load Javascript, but some can. Make sure we don't track XHR or any JS action for bots
        // @link http://stackoverflow.com/a/20084661/2221024
        if (process.env.VUE_APP_JAVASCRIPT_ERROR_LOGGING === 'false' || (/bot|crawl|spider|teoma|slurp|phantomjs/i).test(userAgent())) {
            return window._isJavaScriptErrorLoggingDisabled = true;
        }

        Events.record();

        // Attach our custom error event handler to the global onerror() method.
        window.onerror = this.catcher;

        // Throw any errors that were caught before the application was initialized
        window._errorsCaughtOnLoad.forEach(({
            message, page, line, column, error,
        }) => this.catcher(message, page, line, column, error, true));

        window.onerror = this.catcher;
    }

    /**
     * Catches and builds the error event data and passes it to the critical logger to store in the server database.
     *
     * @param  String  message                    The error message.
     * @param  String  page                       Page URL that the error occurred at.
     * @param  Integer line                       The JavaScript file line number where the error occurred.
     * @param  Integer column                     The JavaScript file column number where the error occurred.
     * @param  Error   error                      The JavaScript Error object.
     * @param  Error   wasBeforeAppInitialization Denotes that the error occurred before the JavaScript application error handler was initialized.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onerror
     */
    async catcher (message, page, line, column, error, wasBeforeAppInitialization = false) {
        // Lazy-load our error reporting modules
        // See https://webpack.js.org/guides/code-splitting/
        const { default: ErrorLogger } = await import('./Logging');
        const { default: StackTrace } = await import('stacktrace-js/stacktrace');
        const { default: mobileAndTabletDeviceDetection } = await import('./../utilities/mobileAndTabletDeviceDetection');

        // Exit error handling if the critical error logger has been disabled due to too many requests per page.
        if (ErrorLogger.isDiabled) {
            return false;
        }

        // Determine if this is a "Script error" message. These generic errors occur when the error is
        // thrown in a script that is hosted on a domain other than the domain of the current page.
        // This can happen for a number of reason such as plugins, browser extensions, users playing in the console.
        // These errors carry no redeeming value for debugging and are therefore permanently silenced.
        // @link http://stackoverflow.com/a/7778424/2221024
        if (message.toLowerCase().indexOf('script error') === 0 && ! _has(error, 'stack')) {
            return;
        }

        // Populate our basic error and meta data.
        const data = {
            url: window.location.href,
            userAgent: userAgent(),
            pageLoadTimestamp: window._pageLoadTimestamp,
            timestamp: Date.now(),
            isMobileOrTablet: Number(mobileAndTabletDeviceDetection()),
            viewport: {
                width: document.documentElement.clientWidth,
                height: document.documentElement.clientHeight,
            },
            language: _has(window, 'navigator.language') ? window.navigator.language : '',
            error: {
                message: message,
                file: page,
                line: line,
                column: column,
                stack: _has(error, 'stack') ? error.stack : [],
            },
            userActions: _cloneDeep(Events.getEvents()),
            stackTrace: [],
            wasBeforeAppInitialization,
        };

        // On the off chance a 'null' error was passed just log what we have and exit the handler.
        if (! error) {
            ErrorLogger.log(data);

            return false;
        }

        // If an Error object exists then we'll use the StackTrace library to build a normalized stack trace for the error.
        let framesResolved = 0;

        // @link https://www.stacktracejs.com/
        StackTrace
            .fromError(error, {
                // Use offline mode so that the stacktrace doesn't attempt to grab the sourcemap file.
                // Sourcemap stack tracing will be handled by the server.
                offline: true,
            })
            .then(stackFrames => {
                // Iterate through the returned stack frames and store the objects into or stackTraces array.
                stackFrames.forEach(stackFrame => {
                    // Store our stack traces to the data.stackTrace array.
                    data.stackTrace.push({
                        line: stackFrame.getLineNumber(),
                        column: stackFrame.getColumnNumber(),
                        file: stackFrame.getFileName(),
                        function: stackFrame.getFunctionName(),
                    });

                    // Create the normalized stack error to help determine duplicate errors.
                    if (framesResolved === 0) {
                        data.error.normalized = stackFrame.toString();
                    }

                    // Increment the resolved frames counter to determine when to send the log data.
                    framesResolved += 1;

                    // Send the data to our critical error logger to pass to the server and database if all stack frames have been resolved.
                    if (framesResolved === stackFrames.length) {
                        ErrorLogger.log(data);
                    }
                });
            })
            // If an error occurs then just log the error data as-is from above.
            .catch(() => ErrorLogger.log(data));

        return false;
    }
}

export default new ErrorHandler();
